// src/components/AiDashboard/AiMyNutritionStatus/AiUser/AiUserInput.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import '../../MyNutritionStatus.css';
import './AiUserCss/AiUserInput.css';
import { AiFillHeart } from 'react-icons/ai';
import { LocalDateIso } from './LocalDateIso';
import {
  FcAnswers,
  FcAssistant,
  FcEngineering,
  FcExpand,
  FcFullTrash,
  FcMakeDecision,
  FcOk,
  FcTodoList,
} from 'react-icons/fc';
import { FaPaperPlane } from 'react-icons/fa';
import { ImFirefox } from 'react-icons/im';
import {
  AiUserInputNutritionDataProvider,
  useAiUserInputData,
  useSectionVisibility,
} from '../AiUserInputNutritionData';
import AiUserInputSeparator from './AiUserInputSeparator/AiUserInputSeparator';

import {
  createAdjustableMealFromNutrition,
  incrementItemPortion,
  decrementItemPortion,
  getDisplayPortion,
  getDisplayQtyMultiplier,
  applyAdjustmentsToNutritionJson,
} from './AiUserInputSeparator/AiMealPortionAdjuster';

// To rebuild Nutrition Facts from adjusted JSON
import { createNutritionFactsFromLastNutrition } from '../AiUserFoodFacts';
import AlertModal from '../../../payments/server/alertModal';

// Firebase uploader for confirmed meals
import AiUserFirebaseConsumptionUploader from './AiUserFirebaseConsumptionUploader';
import { auth, db } from '../../../../firebase/firebaseConfig';
import { Timestamp, doc, getDoc } from 'firebase/firestore';
import AiUserInputCamera from './AiUserInputSeparator/AiUserInputCamera';
import AiUserInputMicrophone from './AiUserInputSeparator/AiUserInputMicrophone';
import {
  parseLabelAndConvertPercentages,
  isLabelText,
} from './AiUserInputSeparator/AiLabelParser';
import AiFoodCatalog, {
  goToNutritionSettings,
} from './AiUserInputSeparator/AiFoodCatalog/AiFoodCatalog';

function isFirefox() {
  if (typeof navigator === 'undefined') return false;
  return navigator.userAgent.toLowerCase().includes('firefox');
}

class WeightLossPlanViewModel {
  static DAYS_PER_WEEK = 7;
  static LOGGING_LOW_COVERAGE_THRESHOLD_PERCENTAGE = 40;
  static LOGGING_GOOD_COVERAGE_THRESHOLD_PERCENTAGE = 80;
  static LOGGING_TARGET_COVERAGE_PERCENTAGE = 70;

  constructor(plan) {
    this.plan = plan || {};
  }

  isFiniteNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
  }

  roundNumber(value, decimals = 0) {
    if (!this.isFiniteNumber(value)) {
      return null;
    }
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
  }

  get currentWeightKilograms() {
    const value = this.plan.currentWeightKilograms;
    return this.isFiniteNumber(value) ? value : null;
  }

  get startWeightKilograms() {
    const value = this.plan.startWeightKilograms;
    return this.isFiniteNumber(value) ? value : null;
  }

  get targetWeightKilograms() {
    const value = this.plan.targetWeightKilograms;
    return this.isFiniteNumber(value) ? value : null;
  }

  get bmiValue() {
    const value = this.plan.bmiValue;
    return this.isFiniteNumber(value) ? value : null;
  }

  get bmiCategory() {
    const category = this.plan.bmiCategory;
    return typeof category === 'string' && category.trim() !== '' ? category.trim() : null;
  }

  get indicativeWeeks() {
    const value = this.plan.indicativeWeeks;
    return this.isFiniteNumber(value) && value > 0 ? value : null;
  }

  get goalKilogramsToLose() {
    const value = this.plan.goalKilogramsToLose;
    return this.isFiniteNumber(value) && value > 0 ? value : null;
  }

  get loggedDays() {
    const value = this.plan.loggedDays;
    return this.isFiniteNumber(value) && value >= 0 ? value : null;
  }

  get rangeDays() {
    const value = this.plan.rangeDays;
    return this.isFiniteNumber(value) && value > 0 ? value : null;
  }

  get estimatedNeedsKilocalories() {
    const value = this.plan.estimatedNeedsKilocalories;
    return this.isFiniteNumber(value) ? value : null;
  }

  get suggestedTargetKilocalories() {
    const value = this.plan.suggestedTargetKilocalories;
    return this.isFiniteNumber(value) ? value : null;
  }

  get deficitKilocalories() {
    const value = this.plan.deficitKilocalories;
    return this.isFiniteNumber(value) ? value : null;
  }

  get weeklyLossKilograms() {
    const value = this.plan.weeklyLossKilograms;
    return this.isFiniteNumber(value) && value > 0 ? value : null;
  }

  get nutrientGapsLabel() {
    const label = this.plan.nutrientGapsLabel;
    return typeof label === 'string' && label.trim() !== '' ? label.trim() : null;
  }

  get weeksElapsedEstimate() {
    if (!this.rangeDays) {
      return null;
    }
    const weeks = this.rangeDays / WeightLossPlanViewModel.DAYS_PER_WEEK;
    return this.isFiniteNumber(weeks) && weeks > 0 ? weeks : null;
  }

  get weeksProgressPercentage() {
    if (!this.indicativeWeeks || !this.weeksElapsedEstimate) {
      return null;
    }
    const rawPercentage = (this.weeksElapsedEstimate / this.indicativeWeeks) * 100;
    const clamped = Math.max(0, Math.min(100, rawPercentage));
    return this.roundNumber(clamped, 0);
  }

  get loggingCoveragePercentage() {
    if (!this.loggedDays || !this.rangeDays) {
      return null;
    }
    const rawPercentage = (this.loggedDays / this.rangeDays) * 100;
    const clamped = Math.max(0, Math.min(100, rawPercentage));
    return this.roundNumber(clamped, 0);
  }

  get goalLineText() {
    if (!this.goalKilogramsToLose) {
      return null;
    }

    const roundedGoal = Math.round(this.goalKilogramsToLose);
    const startWeight = this.startWeightKilograms;
    const targetWeight = this.targetWeightKilograms;

    if (this.isFiniteNumber(startWeight) && this.isFiniteNumber(targetWeight)) {
      return `Goal: Lose **${roundedGoal} kg** (from **${Math.round(
        startWeight,
      )} kg** → **${Math.round(targetWeight)} kg**)`;
    }

    return `Goal: Lose **${roundedGoal} kg**`;
  }

  get timelineSummaryText() {
    if (!this.indicativeWeeks) {
      return null;
    }

    const roundedIndicativeWeeks = Math.round(this.indicativeWeeks);
    const baseText = `Indicative timeline: about **${roundedIndicativeWeeks} weeks** if you follow this plan.`;

    if (!this.weeksElapsedEstimate) {
      return baseText;
    }

    const roundedWeeksElapsed =
      this.weeksElapsedEstimate < 1
        ? '<1'
        : String(Math.max(1, Math.round(this.weeksElapsedEstimate)));

    if (!this.weeksProgressPercentage && this.weeksProgressPercentage !== 0) {
      return `${baseText} You are around **week ${roundedWeeksElapsed}** based on the days in this view.`;
    }

    return `${baseText} You are around **week ${roundedWeeksElapsed}** now (~**${this.weeksProgressPercentage}%** of the way based on the days you have logged).`;
  }

  get loggingTaskText() {
    if (!this.rangeDays || this.rangeDays <= 0) {
      return 'Log every meal and snack so your goal timeline stays accurate.';
    }

    if (this.loggedDays === 0 || this.loggedDays === null) {
      return 'You have not logged any meals yet in this period. Start logging today so we can show your progress along the timeline.';
    }

    const coveragePercentage = this.loggingCoveragePercentage;
    const roundedCoverage = coveragePercentage != null ? coveragePercentage : null;

    if (roundedCoverage === null) {
      return `You have logged **${this.loggedDays} of ${this.rangeDays} days**. Keep logging so the progress view becomes more precise.`;
    }

    const targetDays = Math.max(
      this.loggedDays + 1,
      Math.round(
        (WeightLossPlanViewModel.LOGGING_TARGET_COVERAGE_THRESHOLD_PERCENTAGE / 100) *
        this.rangeDays,
      ),
    );

    if (roundedCoverage < WeightLossPlanViewModel.LOGGING_LOW_COVERAGE_THRESHOLD_PERCENTAGE) {
      return `You have logged **${this.loggedDays} of ${this.rangeDays} days (~${roundedCoverage}%)**. Aim to reach at least **${targetDays} logged days** in this period so your progress line becomes meaningful.`;
    }

    if (roundedCoverage < WeightLossPlanViewModel.LOGGING_GOOD_COVERAGE_THRESHOLD_PERCENTAGE) {
      return `You have logged **${this.loggedDays} of ${this.rangeDays} days (~${roundedCoverage}%)**. This gives a rough picture; logging a little more will make your weekly trend much clearer.`;
    }

    return `Great work logging **${this.loggedDays} of ${this.rangeDays} days (~${roundedCoverage}%)**. Keeping this level of consistency makes your goal timeline very reliable.`;
  }

  get weeklyFocusTaskText() {
    if (!this.goalKilogramsToLose || !this.indicativeWeeks) {
      return null;
    }

    const averageKilogramsPerWeek = this.goalKilogramsToLose / this.indicativeWeeks;
    const roundedAverageKilogramsPerWeek = this.roundNumber(averageKilogramsPerWeek, 1);

    if (!this.isFiniteNumber(roundedAverageKilogramsPerWeek)) {
      return null;
    }

    const targetIntake = this.suggestedTargetKilocalories;
    const deficit = this.deficitKilocalories;

    if (this.isFiniteNumber(targetIntake) && this.isFiniteNumber(deficit)) {
      return `This week, aim for about **${Math.round(
        targetIntake,
      )} kcal per day** (around **${Math.round(
        deficit,
      )} kcal below your needs**) so you average roughly **${roundedAverageKilogramsPerWeek} kg loss per week**. See your nutrition status in BMI & Body Fat for details (look at **Average logged intake**).`;
    }

    return `This week, focus on habits that move you toward roughly **${roundedAverageKilogramsPerWeek} kg loss per week**, while keeping protein and fibre close to 100% of your targets.`;
  }
}

function renderEmphasized(text) {
  if (text == null) {
    return null;
  }

  const parts = String(text).split('**');

  if (parts.length === 1) {
    return text;
  }

  return parts.map((part, index) =>
    index % 2 === 1 ? <strong key={index}>{part}</strong> : part,
  );
}

function WeightLossPlanCard({ title, plan }) {
  if (!plan) {
    return null;
  }

  const viewModel = new WeightLossPlanViewModel(plan);

  const isDisplayableNumber = (value) =>
    typeof value === 'number' && Number.isFinite(value);

  const hasText = (value) => typeof value === 'string' && value.trim() !== '';

  const canShowWeightBmi =
    isDisplayableNumber(viewModel.currentWeightKilograms) &&
    (isDisplayableNumber(viewModel.bmiValue) || hasText(viewModel.bmiCategory));

  const canShowCalories =
    isDisplayableNumber(viewModel.estimatedNeedsKilocalories) &&
    isDisplayableNumber(viewModel.suggestedTargetKilocalories);

  const canShowGoal = isDisplayableNumber(viewModel.goalKilogramsToLose);

  return (
    <div className="phn-weightloss-card aiu-card text-start">
      <div className="phn-weightloss-card-title text-center fw-bold mb-2">
        {title}
      </div>

      {canShowWeightBmi && (
        <div className="phn-weightloss-card-section">
          <div className="fw-semibold">Weight &amp; BMI</div>
          <div>
            Current weight{' '}
            <strong>{Math.round(viewModel.currentWeightKilograms)} kg</strong>
            {viewModel.bmiValue != null && (
              <>
                , BMI <strong>{viewModel.bmiValue.toFixed(1)}</strong>
                {viewModel.bmiCategory && (
                  <>
                    {' '}
                    (
                    <strong>{viewModel.bmiCategory}</strong>
                    )
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {canShowCalories && (
        <div className="phn-weightloss-card-section">
          <div className="fw-semibold">Calorie target</div>
          <div>
            Estimated daily need{' '}
            ≈ <strong>{Math.round(viewModel.estimatedNeedsKilocalories)} kcal/day</strong>
          </div>
          <div>
            Suggested target{' '}
            ≈ <strong>{Math.round(viewModel.suggestedTargetKilocalories)} kcal/day</strong>
            {isDisplayableNumber(viewModel.deficitKilocalories) && (
              <>
                {' '}
                (about{' '}
                <strong>{Math.round(viewModel.deficitKilocalories)} kcal below your needs</strong>)
              </>
            )}
          </div>
          {isDisplayableNumber(viewModel.weeklyLossKilograms) && (
            <div>
              This corresponds to a steady weight-loss pace of about{' '}
              <strong>{viewModel.weeklyLossKilograms.toFixed(1)} kg per week</strong>.
            </div>
          )}
        </div>
      )}

      {canShowGoal && (
        <div className="phn-weightloss-card-section">
          <div className="fw-semibold">Goal &amp; timeline</div>
          {viewModel.goalLineText && (
            <div>{renderEmphasized(viewModel.goalLineText)}</div>
          )}
          {viewModel.timelineSummaryText && (
            <div>{renderEmphasized(viewModel.timelineSummaryText)}</div>
          )}
        </div>
      )}

      <div className="phn-weightloss-card-section">
        <div className="fw-semibold">Key habits</div>
        <ul className="mb-0 ps-3">
          <li>Keep protein and fibre close to 100% of your targets most days.</li>
          <li>
            Stay in a small calorie deficit: if your weight is not trending down ~0.25–0.75% per
            week over a month, reduce portions slightly or cut 1–2 calorie-dense extras per day.
          </li>
          <li>
            Watch total fat: aim to keep your daily total fat around your target and avoid
            regularly going far above it, since oils, nuts, seeds, mayo and bacon are very calorie
            dense.
          </li>
          <li>
            Measure added fats instead of “eyeballing” them (oil, butter, mayo, nut butters) – even
            1–2 extra tablespoons a day can stall weight loss.
          </li>
          <li>
            Prefer whole-food fats (nuts, seeds, avocado, oily fish) in small portions over
            deep-fried foods, pastries and fast food, which combine a lot of fat and refined carbs.
          </li>
          <li>
            Keep saturated fat and trans fat low most days away (fatty red meats, processed meats,
            commercial pastries, fast food) to support heart health while you lose weight.
          </li>
          <li>
            Limit liquid calories (sugary drinks, juice, fancy coffees, alcohol). Choose water,
            tea, coffee without sugar, or low-/no-calorie drinks most of the time.
          </li>
          <li>
            Fill at least half your plate with low-calorie, high-fibre vegetables at 1–2 meals per
            day to stay fuller on fewer calories.
          </li>
          <li>
            Choose slow-digesting carbs most of the time (whole grains, beans, lentils, potatoes
            with skin) instead of refined starches and sweets.
          </li>
          <li>
            If you feel very tired, cold, and gain weight despite hitting your targets and staying
            in a deficit, talk to your doctor about thyroid and iodine status instead of just
            cutting more calories.
          </li>
          {viewModel.nutrientGapsLabel && (
            <li>
              Start improving low nutrients (
              <strong>{viewModel.nutrientGapsLabel}</strong>).
            </li>
          )}
          <li>{renderEmphasized(viewModel.loggingTaskText)}</li>
          {viewModel.weeklyFocusTaskText && (
            <li>{renderEmphasized(viewModel.weeklyFocusTaskText)}</li>
          )}
        </ul>
      </div>
    </div>
  );
}

function AiUserInputView() {
  const navigate = useNavigate();

  const {
    serviceName,
    activeDateIso,
    uiConfig,
    sizeToPadding,
    routes,
    labels,
    macros,
    vitMin,
    vmHeadlineChip,
    consumptionLog,
    suggestedLog,
    results,
    chips,
    nutritionFacts,
    onConfirm,
    onSendText,
    latestMinimalJson,
    latestNutritionJson,
    dailyValueReference,
    userProfile,
    weightLossPlan,
    showWeightLossPlan,
    setShowWeightLossPlan,
  } = useAiUserInputData();

  const hasActiveWeightLossPlan = React.useMemo(() => {
    return Boolean(
      weightLossPlan &&
      weightLossPlan.hasWeightLossGoal &&
      weightLossPlan.currentWeightKilograms != null &&
      weightLossPlan.goalKilogramsToLose != null &&
      weightLossPlan.goalKilogramsToLose > 0,
    );
  }, [weightLossPlan]);

  const weightLossPlanTitle =
    typeof labels?.weightLossPlanTitle === 'string' && labels.weightLossPlanTitle.trim() !== ''
      ? labels.weightLossPlanTitle
      : 'Weight Loss Plan';

  const [inputValue, setInputValue] = React.useState('');
  const textareaRef = React.useRef(null);

  const [hasAttachedImage, setHasAttachedImage] = React.useState(false);

  const [cameraStatus, setCameraStatus] = React.useState('idle');

  const [microphoneStatus, setMicrophoneStatus] = React.useState('idle');

  const [hasCompletedAiRound, setHasCompletedAiRound] = React.useState(false);

  const [hasUserTriggeredRound, setHasUserTriggeredRound] = React.useState(false);

  const [devJsonOpen] = React.useState(false);

  const [localMinimalJson, setLocalMinimalJson] = React.useState(null);
  const [localNutritionJson, setLocalNutritionJson] = React.useState(null);
  const [latestNutritionResult, setLatestNutritionResult] = React.useState(null);
  const [nutritionModalItem, setNutritionModalItem] = React.useState(null);

  const [aiLoading, setAiLoading] = React.useState(false);

  const [isFirefoxBrowser, setIsFirefoxBrowser] = React.useState(false);

  const [showHelpModal, setShowHelpModal] = React.useState(false);

  const [showMacrosInfoModal, setShowMacrosInfoModal] = React.useState(false);

  const [showVitaminsInfoModal, setShowVitaminsInfoModal] = React.useState(false);

  const [showSuggestedInfoModal, setShowSuggestedInfoModal] = React.useState(false);

  // eslint-disable-next-line no-unused-vars
  const [selectedFoodCategory, setSelectedFoodCategory] = React.useState({
    category: 'all',
    tags: [],
  });

  /* eslint-disable-next-line no-unused-vars */
  const [selectedDietaryFilters, setSelectedDietaryFilters] = React.useState([]);

  const previousActiveDateIsoRef = React.useRef(activeDateIso);

  React.useEffect(() => {
    const previous = previousActiveDateIsoRef.current;

    if (previous && activeDateIso && previous !== activeDateIso) {
      setInputValue('');
      setLocalMinimalJson(null);
      setLocalNutritionJson(null);
      setLatestNutritionResult(null);
      setNutritionModalItem(null);
      setHasAttachedImage(false);
      setHasCompletedAiRound(false);
      setHasUserTriggeredRound(false);
    }

    previousActiveDateIsoRef.current = activeDateIso;
  }, [activeDateIso]);

  const userDeficiencies = React.useMemo(() => {
    if (!vitMin || !vitMin.deficiencies) return [];

    return vitMin.deficiencies.map((def) => ({
      nutrient: def.name,
      amount: def.amount,
      unit: def.unit,
      percentage: def.percentage,
    }));
  }, [vitMin]);

  const handleFoodCategoryChange = React.useCallback((categoryData) => {
    setSelectedFoodCategory(categoryData);
  }, []);

  const handleDietaryFilterChange = React.useCallback((filters) => {
    setSelectedDietaryFilters(filters);
  }, []);

  React.useEffect(() => {
    setIsFirefoxBrowser(isFirefox());
  }, []);

  React.useEffect(() => {
    void dailyValueReference;
  }, [dailyValueReference]);

  const autoResizeTextarea = React.useCallback(() => {
    const element = textareaRef.current;
    if (!element) return;

    const maxRows = Number(element.dataset.maxRows || 6);
    const lineHeight = parseFloat(getComputedStyle(element).lineHeight) || 20;
    const maxHeight = Math.ceil(lineHeight * maxRows);

    element.style.height = 'auto';
    const newHeight = Math.min(maxHeight, element.scrollHeight);
    element.style.height = `${newHeight}px`;
    element.style.overflowY = element.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  React.useLayoutEffect(() => {
    autoResizeTextarea();
  }, [inputValue, autoResizeTextarea]);

  React.useEffect(() => {
    const onResize = () => autoResizeTextarea();
    window.addEventListener('resize', onResize, { passive: true });

    return () => window.removeEventListener('resize', onResize);
  }, [autoResizeTextarea]);

  const sendAriaLabel =
    labels?.sendBtnAriaLabel ?? labels?.confirmBtnAriaLabel ?? 'Send';
  const sendTitle = labels?.sendBtnTitle ?? labels?.confirmBtnTitle ?? 'Send';
  const aiAssistTitle = labels?.aiAssistStatusTitle || 'AI assistance';
  const aiAssistSubtitle =
    labels?.aiAssistStatusSubtitle || 'Communicating with AI assistant…';

  const cameraModePlaceholder =
    labels?.cameraModePlaceholder || 'Click to send image to AI...';

  const handleCameraImageChange = React.useCallback(({ file, previewUrl }) => {
    void previewUrl;
    setHasAttachedImage(!!file);
  }, []);


  const handleCameraImageTextReady = React.useCallback(
    ({ text, error, rawResponse, eventMeta }) => {
      void rawResponse;
      void eventMeta;

      if (error) {
        setCameraStatus('idle');
      }

      const trimmedText = typeof text === 'string' ? text.trim() : '';

      if (!trimmedText) {
        setCameraStatus('idle');
        return;
      }

      setInputValue(trimmedText);
      setHasCompletedAiRound(false);
      setCameraStatus('idle');

      requestAnimationFrame(() => {
        if (typeof autoResizeTextarea === 'function') {
          autoResizeTextarea();
        }
      });
    },
    [autoResizeTextarea],
  );

  const handleMicrophoneTranscriptReady = React.useCallback(
    ({ text, error, rawResponse, eventMeta }) => {
      void rawResponse;
      void eventMeta;

      if (error) {
        setMicrophoneStatus('idle');
      }

      const trimmedText = typeof text === 'string' ? text.trim() : '';

      if (!trimmedText) {
        setMicrophoneStatus('idle');
        return;
      }

      setInputValue((previousValue) => {
        const currentTrimmed = previousValue.trim();
        return currentTrimmed ? `${currentTrimmed} ${trimmedText}` : trimmedText;
      });

      setHasCompletedAiRound(false);
      setMicrophoneStatus('idle');

      requestAnimationFrame(() => {
        if (typeof autoResizeTextarea === 'function') {
          autoResizeTextarea();
        }
      });
    },
    [autoResizeTextarea],
  );


  const handleCameraStatusChange = React.useCallback(({ status }) => {
    setCameraStatus(status);
  }, []);


  const handleMicrophoneStatusChange = React.useCallback(({ status }) => {
    setMicrophoneStatus(status);
  }, []);


  const handleSend = async (triggerType = 'send_button') => {
    const currentInputValue = String(inputValue ?? '');
    const trimmedInputText = currentInputValue.trim();

    if (!trimmedInputText) return;

    setHasCompletedAiRound(false);
    setHasUserTriggeredRound(true);
    setInputValue('');
    setHasAttachedImage(false);
    requestAnimationFrame(autoResizeTextarea);

    let textToSend = trimmedInputText;

    if (isLabelText(trimmedInputText)) {
      const originalText = trimmedInputText;
      textToSend = parseLabelAndConvertPercentages(trimmedInputText, dailyValueReference);

      void originalText;
      void textToSend;
      void dailyValueReference;
    }

    let ingestAccepted = true;

    try {
      const ingestResult = await AiUserInputSeparator.ingestUserText({
        text: textToSend,
        entityId: 'user_free_text',
        sourceServiceName: 'AiUserInput',
        ingress: 'user_input_ingress',
        egress: 'separator_ingress',
      });

      const minimal = ingestResult?.minimal || AiUserInputSeparator.getLatestMinimal();

      if (minimal) {
        setLocalMinimalJson(minimal);
      }

      if (Object.prototype.hasOwnProperty.call(ingestResult || {}, 'accepted')) {
        ingestAccepted = !!ingestResult.accepted;
      }
    } catch (error) {
      void error;
      ingestAccepted = true;
    }

    let upstreamResponse = null;
    setAiLoading(true);

    try {
      if (typeof onSendText === 'function') {
        upstreamResponse = await onSendText({
          text: trimmedInputText,
          triggerType,
          minimalInputJson: latestMinimalJson || localMinimalJson || null,
        });
      }
    } catch (error) {
      void error;
    } finally {
      setAiLoading(false);
    }

    if (upstreamResponse) {
      const maybeNutritionJson =
        upstreamResponse?.nutrition?.json ||
        upstreamResponse?.nutritionJson ||
        upstreamResponse?.json ||
        null;

      if (maybeNutritionJson) {
        setLocalNutritionJson(maybeNutritionJson);
      }

      setLatestNutritionResult(upstreamResponse);
    }

    void ingestAccepted;
  };

  const handleTextareaKeyDown = async (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (String(inputValue ?? '').trim()) {
        await handleSend('enter_key');
      }
    }
  };


  const {
    showVitamins,
    showConsumption,
    showSuggested,
    setShowVitamins,
    setShowConsumption,
    setShowSuggested,
  } = useSectionVisibility();

  const [expanded, setExpanded] = React.useState(() => new Set());
  const [deletedItemIds, setDeletedItemIds] = React.useState(() => new Set());
  const [deletedAdjustItems, setDeletedAdjustItems] = React.useState([]);

  const ctaPadding = sizeToPadding[uiConfig.size] || sizeToPadding.md;
  const ctaClass = `btn btn-${uiConfig.ctaVariant} rounded-pill fw-bold ${ctaPadding} aiu-confirm-btn`;

  const toggleIngredients = (id) => {
    setExpanded((previousExpanded) => {
      const nextExpanded = new Set(previousExpanded);
      if (nextExpanded.has(id)) {
        nextExpanded.delete(id);
      } else {
        nextExpanded.add(id);
      }
      return nextExpanded;
    });
  };

  const [mealAdjustState, setMealAdjustState] = React.useState(null);

  React.useEffect(() => {
    if (latestNutritionJson && Array.isArray(latestNutritionJson.consumed)) {
      setMealAdjustState(createAdjustableMealFromNutrition(latestNutritionJson));
    } else {
      setMealAdjustState(null);
    }
  }, [latestNutritionJson]);

  React.useEffect(() => {
    setDeletedItemIds(new Set());
    setDeletedAdjustItems([]);
  }, [latestNutritionJson, results]);

  const handleIncrementPortion = (itemId) => {
    setMealAdjustState((previousState) => {
      if (!previousState) return previousState;
      return incrementItemPortion(previousState, itemId);
    });
  };

  const handleDecrementPortion = (itemId) => {
    setMealAdjustState((previousState) => {
      if (!previousState) return previousState;
      return decrementItemPortion(previousState, itemId);
    });
  };

  const handleDeleteItem = React.useCallback(
    (itemId) => {
      setDeletedItemIds((previous) => {
        const next = new Set(previous);
        next.add(itemId);
        return next;
      });

      setDeletedAdjustItems((previous) => {
        const remaining = previous.filter((item) => item.id !== itemId);
        const removed =
          mealAdjustState?.items?.find((candidate) => candidate.id === itemId) || null;

        if (removed) {
          return [...remaining, removed];
        }

        return remaining;
      });

      setMealAdjustState((previousState) => {
        if (!previousState) return previousState;
        const nextItems = previousState.items.filter((item) => item.id !== itemId);
        return { ...previousState, items: nextItems };
      });
    },
    [mealAdjustState],
  );

  const handleRestoreDeletedItems = React.useCallback(() => {
    if (deletedItemIds.size === 0 && deletedAdjustItems.length === 0) return;

    setMealAdjustState((previousState) => {
      if (!previousState) {
        if (latestNutritionJson) {
          return createAdjustableMealFromNutrition(latestNutritionJson);
        }
        return previousState;
      }

      const combined = new Map();

      const pushItem = (item) => {
        if (item && item.id) combined.set(item.id, item);
      };

      (previousState.items || []).forEach(pushItem);
      (deletedAdjustItems || []).forEach(pushItem);

      const orderedItems = (results || [])
        .map((resultItem) => combined.get(resultItem.id))
        .filter(Boolean);

      for (const item of combined.values()) {
        if (!orderedItems.includes(item)) {
          orderedItems.push(item);
        }
      }

      return { ...previousState, items: orderedItems };
    });

    setDeletedItemIds(new Set());
    setDeletedAdjustItems([]);
  }, [deletedAdjustItems, deletedItemIds, latestNutritionJson, results]);

  const hasDeletions = deletedItemIds.size > 0;

  const filteredMealAdjustState = React.useMemo(() => {
    if (!mealAdjustState) return null;

    const filteredItems = Array.isArray(mealAdjustState.items)
      ? mealAdjustState.items.filter((item) => !deletedItemIds.has(item.id))
      : [];

    return { ...mealAdjustState, items: filteredItems };
  }, [mealAdjustState, deletedItemIds]);

  const nutritionJsonAfterDeletes = React.useMemo(() => {
    if (!latestNutritionJson) return null;
    if (!deletedItemIds.size) return latestNutritionJson;

    const filteredConsumed = Array.isArray(latestNutritionJson.consumed)
      ? latestNutritionJson.consumed.filter((consumedItem) => !deletedItemIds.has(consumedItem.id))
      : [];

    return { ...latestNutritionJson, consumed: filteredConsumed };
  }, [latestNutritionJson, deletedItemIds]);

  const adjustedNutritionJson = React.useMemo(() => {
    if (!nutritionJsonAfterDeletes || !filteredMealAdjustState) return null;

    return applyAdjustmentsToNutritionJson(nutritionJsonAfterDeletes, filteredMealAdjustState);
  }, [nutritionJsonAfterDeletes, filteredMealAdjustState]);

  const adjustedNutritionFacts = React.useMemo(() => {
    if (!adjustedNutritionJson) return null;
    return createNutritionFactsFromLastNutrition(adjustedNutritionJson);
  }, [adjustedNutritionJson]);

  const effectiveNutritionFacts = adjustedNutritionFacts || nutritionFacts;

  const consumedNutritionLookup = React.useMemo(() => {
    const consumedArray =
      (adjustedNutritionJson && adjustedNutritionJson.consumed) ||
      (nutritionJsonAfterDeletes && nutritionJsonAfterDeletes.consumed) ||
      (latestNutritionJson && latestNutritionJson.consumed) ||
      (localNutritionJson && localNutritionJson.consumed) ||
      [];

    const map = new Map();

    if (Array.isArray(consumedArray)) {
      consumedArray.forEach((item) => {
        if (item && item.id) {
          map.set(item.id, item);
        }
      });
    }

    return map;
  }, [adjustedNutritionJson, nutritionJsonAfterDeletes, latestNutritionJson, localNutritionJson]);

  const visibleResults = React.useMemo(
    () => (results || []).filter((item) => !deletedItemIds.has(item.id)),
    [results, deletedItemIds],
  );

  const openNutritionModalForItem = React.useCallback(
    (itemId) => {
      const consumedItem = consumedNutritionLookup.get(itemId);

      if (!consumedItem) {
        setNutritionModalItem({
          title: 'Nutrition unavailable',
          portion: null,
          nutrients: null,
        });
        return;
      }

      const portion = consumedItem.portion || {};
      const cleanPortion = {
        quantity: portion.quantity,
        unit: portion.unit,
        gramEquivalent: portion.gramEquivalent,
      };

      setNutritionModalItem({
        title: consumedItem.label || consumedItem.id || 'Nutrition',
        portion: cleanPortion,
        nutrients: consumedItem.nutrients || {},
      });
    },
    [consumedNutritionLookup],
  );


  const buildServingsPayload = React.useCallback(() => {
    if (
      filteredMealAdjustState &&
      Array.isArray(filteredMealAdjustState.items) &&
      filteredMealAdjustState.items.length > 0
    ) {
      return filteredMealAdjustState.items.reduce((servingsMap, adjustableItem) => {
        const safeScale =
          typeof adjustableItem.scale === 'number' && Number.isFinite(adjustableItem.scale)
            ? adjustableItem.scale
            : 1;

        servingsMap[adjustableItem.id] = safeScale;
        return servingsMap;
      }, {});
    }

    return (visibleResults || []).reduce((servingsMap, item) => {
      servingsMap[item.id] = 1;
      return servingsMap;
    }, {});
  }, [filteredMealAdjustState, visibleResults]);

  const hasValidAiNutrition = React.useMemo(() => {
    const data =
      adjustedNutritionJson || nutritionJsonAfterDeletes || latestNutritionJson || localNutritionJson;

    if (!data) return false;

    const consumedArray = Array.isArray(data.consumed) ? data.consumed : null;
    return Array.isArray(consumedArray) && consumedArray.length > 0;
  }, [adjustedNutritionJson, nutritionJsonAfterDeletes, latestNutritionJson, localNutritionJson]);

  React.useEffect(() => {
    if (hasUserTriggeredRound && hasValidAiNutrition && !aiLoading) {
      setHasCompletedAiRound(true);
    }
  }, [hasUserTriggeredRound, hasValidAiNutrition, aiLoading]);

  const shouldShowConfirmButton = !aiLoading && hasCompletedAiRound && hasValidAiNutrition;

  const [isConfirmAlertOpen, setIsConfirmAlertOpen] = React.useState(false);
  const [isConfirming, setIsConfirming] = React.useState(false);
  const [pendingConfirmedMeal, setPendingConfirmedMeal] = React.useState(null);

  const openConfirmAlert = React.useCallback(() => {
    const servingsPayload = buildServingsPayload();
    const itemsPayload = visibleResults || [];

    setPendingConfirmedMeal({
      servings: servingsPayload,
      items: itemsPayload,
    });
    setIsConfirmAlertOpen(true);
  }, [buildServingsPayload, visibleResults]);

  const closeConfirmAlert = React.useCallback(() => {
    setIsConfirmAlertOpen(false);
  }, []);

  const handleConfirmConsumptionFromAlert = React.useCallback(async () => {
    if (!pendingConfirmedMeal) {
      setIsConfirmAlertOpen(false);
      return;
    }

    setIsConfirming(true);

    if (typeof onConfirm === 'function') {
      try {
        await onConfirm(pendingConfirmedMeal);
      } catch (error) {
        void error;
      }
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setIsConfirming(false);
      return;
    }

    try {
      const nutritionSnapshot = adjustedNutritionJson || latestNutritionJson || localNutritionJson;

      if (!nutritionSnapshot) {
        setIsConfirming(false);
        return;
      }

      const entityId = `meal-${Date.now()}-${currentUser.uid}`;

      const uploadResult = await AiUserFirebaseConsumptionUploader.uploadConsumptionLog({
        userId: currentUser.uid,
        entityId,
        servings: pendingConfirmedMeal.servings,
        items: pendingConfirmedMeal.items,
        nutritionJsonSnapshot: {
          ...nutritionSnapshot,
          loggedAt: Timestamp.now(),
          uiAdjustmentsApplied: !!(adjustedNutritionJson && mealAdjustState),
        },
        metadata: {
          requestId: crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
          traceId: crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
          visited: [serviceName || 'AiUserInput'],
          hopCount: 0,
        },
      });

      const dateIso = activeDateIso || LocalDateIso.today();

      const dailyIntakeRef = doc(db, 'users', currentUser.uid, 'dailyIntake', dateIso);

      let initialSnapshot = null;

      try {
        initialSnapshot = await getDoc(dailyIntakeRef);
      } catch (error) {
        void error;
      }

      const initialData = initialSnapshot?.exists() ? initialSnapshot.data() : null;
      const initialUpdatedAt = initialData?.updatedAt?.toMillis?.() || 0;

      const maxPollingTimeMs = 5000;
      const pollingIntervalMs = 500;
      const startTime = Date.now();
      let updateDetected = false;

      while (Date.now() - startTime < maxPollingTimeMs && !updateDetected) {
        await new Promise((resolve) => setTimeout(resolve, pollingIntervalMs));

        try {
          const currentSnapshot = await getDoc(dailyIntakeRef);
          if (currentSnapshot.exists()) {
            const currentData = currentSnapshot.data();
            const currentUpdatedAt = currentData?.updatedAt?.toMillis?.() || 0;

            if (currentUpdatedAt > initialUpdatedAt) {
              updateDetected = true;
            }
          }
        } catch (error) {
          void error;
        }
      }

      if (uploadResult.outcome === 'success' || uploadResult.outcome === 'duplicate') {
        window.location.reload();
      } else {
        setIsConfirming(false);
      }
    } catch (error) {
      void error;
      setIsConfirming(false);
    }
  }, [
    onConfirm,
    pendingConfirmedMeal,
    adjustedNutritionJson,
    latestNutritionJson,
    localNutritionJson,
    serviceName,
    mealAdjustState,
    activeDateIso,
  ]);

  const hasText = !!String(inputValue ?? '').trim();

  const isCameraSendModeActive = hasAttachedImage;

  const isTextInputDisabled = isCameraSendModeActive;

  const getEffectivePlaceholder = () => {
    if (isCameraSendModeActive) {
      return cameraModePlaceholder;
    }

    switch (microphoneStatus) {
      case 'preparing':
        return 'Firefox requesting access...';
      case 'recording':
        return 'Click & Hold Connecting...';
      case 'ready_to_speak':
        return 'Now you may talk...';
      case 'processing':
        return aiAssistSubtitle;
      default:
        return labels.inputPlaceholder;
    }
  };

  const effectiveInputPlaceholder = getEffectivePlaceholder();

  const getTextareaClassName = () => {
    const baseClass = 'form-control aiu-textarea border-0 shadow-none';

    if (microphoneStatus === 'recording') {
      return `${baseClass} mic-connecting`;
    }

    if (microphoneStatus === 'ready_to_speak') {
      return `${baseClass} mic-ready`;
    }

    return baseClass;
  };

  const formatNutrientKey = (key) => {
    if (!key || typeof key !== 'string') return String(key);

    const parts = key.split('_');

    const possibleUnit = parts[parts.length - 1];
    const unitRegex = /^(kcal|g|mg|µg|ug|kj|NE|aTE|RE)$/i;

    if (parts.length > 1 && unitRegex.test(possibleUnit)) {
      const name = parts.slice(0, -1).join(' ');
      return `${name.replace(/\b\w/g, (m) => m.toUpperCase())} (${possibleUnit})`;
    }

    return parts.join(' ').replace(/\b\w/g, (m) => m.toUpperCase());
  };

  const formatNutrientValue = (val) => {
    if (val === null || typeof val === 'undefined') return '—';
    if (typeof val === 'number') {
      if (Math.abs(val - Math.round(val)) < 0.01) return String(Math.round(val));
      return String(Number(val.toFixed(2)));
    }
    return String(val);
  };

  return (
    <div
      className="aiu-root phn-nutrition notranslate"
      data-density={uiConfig.density}
      data-size={uiConfig.size}
      data-service={serviceName}
      translate="no"
    >
      <div className="topbar">
        <div className="container topbar-content">
          <div className="spacer" />
        </div>
      </div>

      <div className="aiu-shell">
        {/* Header */}
        <header className="aiu-header">
          <div className="aiu-header-inner">
            <h1 className="aiu-header-title text-center m-0">{labels.headerTitle}</h1>

            <button
              type="button"
              onClick={() => navigate(routes.myNutritionStatus)}
              aria-label={labels.heartBtnAriaLabel}
              title={labels.heartBtnTitle}
              className="aiu-heart-btn"
            >
              <span className="aiu-like aiu-like-pulse" tabIndex={-1} aria-hidden="true">
                <AiFillHeart className="aiu-like-icon" />
              </span>
            </button>
          </div>
        </header>

        <main className="p-3 pb-5 aiu-main">
          {hasActiveWeightLossPlan && weightLossPlan && (
            <section className="mb-2">
              <div className="d-flex align-items-center justify-content-between mb-2 aiu-row-48">
                <div className="d-flex align-items-center gap-2">
                  <h2 className="aiu-section-title m-0">{weightLossPlanTitle}</h2>
                </div>

                <div className="d-flex align-items-center gap-3">
                  <Switch
                    checked={showWeightLossPlan}
                    onChange={() => setShowWeightLossPlan(!showWeightLossPlan)}
                    a11yLabel={labels.toggleSectionAriaLabel}
                    title={labels.toggleSectionTitle}
                  />
                </div>
              </div>

              <div className={`aiu-collapsible ${showWeightLossPlan ? 'is-open' : 'is-closed'}`}>
                <div className="mb-2">
                  <WeightLossPlanCard title={weightLossPlanTitle} plan={weightLossPlan} />
                </div>
              </div>
            </section>
          )}
          {/* Macros */}
          <section className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-2 aiu-row-48">
              <div className="d-flex align-items-center gap-2">
                <h2 className="aiu-section-title m-0">{labels.macrosTitle}</h2>

                <button
                  type="button"
                  onClick={() => setShowMacrosInfoModal(true)}
                  aria-label="Learn about your macros"
                  title="Learn about your macros"
                  style={{
                    lineHeight: 1,
                    fontSize: '1.25rem',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <FcAnswers />
                </button>
              </div>
            </div>

            <div className="container-fluid px-0">
              <div className="row g-2">
                {macros.map((macroItem) => {
                  let statusClass = 'aiu-bar-fill-neutral';
                  const pctValue =
                    typeof macroItem.pct === 'number' && Number.isFinite(macroItem.pct)
                      ? macroItem.pct
                      : null;
                  const percentage = pctValue ?? 0;
                  const hasTarget = macroItem.target != null && macroItem.target > 0;

                  if (hasTarget && pctValue != null) {
                    if (percentage < 80) {
                      statusClass = 'aiu-bar-fill-low';
                    } else if (percentage <= 120) {
                      statusClass = 'aiu-bar-fill-good';
                    } else if (percentage <= 150) {
                      statusClass = 'aiu-bar-fill-warn';
                    } else {
                      statusClass = 'aiu-bar-fill-danger';
                    }
                  }

                  return (
                    <div key={macroItem.id} className="col-3">
                      <div className="aiu-metric">
                        <div className="aiu-metric-title">{macroItem.label}</div>

                        <div className="aiu-metric-value">
                          {macroItem.value == null ? '—' : macroItem.value}
                          {macroItem.target != null ? (
                            <span className="aiu-muted ms-1">
                              /{macroItem.target} {macroItem.unit ?? ''}
                            </span>
                          ) : macroItem.unit ? (
                            <span className="aiu-muted ms-1">{macroItem.unit}</span>
                          ) : null}
                        </div>

                        <div className="aiu-bar">
                          <span
                            className={`aiu-bar-fill ${statusClass} aiu-w-${Math.min(
                              120,
                              Math.max(0, Math.round(percentage)),
                            )}`}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Vitamins & Minerals */}
          <section className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-2 aiu-row-48">
              <div className="d-flex align-items-center gap-2">
                <h2 className="aiu-section-title m-0">{labels.vitaminsTitle}</h2>

                <button
                  type="button"
                  onClick={() => setShowVitaminsInfoModal(true)}
                  aria-label="Learn about vitamins and minerals"
                  title="Learn about vitamins and minerals"
                  style={{
                    lineHeight: 1,
                    fontSize: '1.25rem',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <FcAnswers />
                </button>
              </div>

              <div className="d-flex align-items-center gap-3">
                <div
                  className={`aiu-chip ${showVitamins || !vmHeadlineChip?.isVisible ? 'opacity-0' : ''
                    }`}
                >
                  {vmHeadlineChip?.text || ''}
                </div>

                <Switch
                  checked={showVitamins}
                  onChange={() => setShowVitamins(!showVitamins)}
                  a11yLabel={labels.toggleSectionAriaLabel}
                  title={labels.toggleSectionTitle}
                />
              </div>
            </div>

            <div className={`aiu-collapsible ${showVitamins ? 'is-open' : 'is-closed'}`}>
              <div className="container-fluid px-0">
                <div className="row g-2">
                  {(Array.isArray(vitMin) ? vitMin : []).map((card) => {
                    const statusClass =
                      card.status === 'alert'
                        ? 'aiu-vm-alert'
                        : card.status === 'warn'
                          ? 'aiu-vm-warn'
                          : card.status === 'over'
                            ? 'aiu-vm-over'
                            : card.status === 'good'
                              ? 'aiu-vm-good'
                              : 'aiu-vm-neutral';

                    const fillClass =
                      card.status === 'alert'
                        ? 'aiu-vm-fill-alert'
                        : card.status === 'warn'
                          ? 'aiu-vm-fill-warn'
                          : card.status === 'over'
                            ? 'aiu-vm-fill-over'
                            : card.status === 'good'
                              ? 'aiu-vm-fill-good'
                              : 'aiu-vm-fill-neutral';

                    const barWidth =
                      card.percentOfTarget == null
                        ? 0
                        : Math.min(120, Math.max(0, card.percentOfTarget));

                    return (
                      <div key={card.id} className="col-2">
                        <div className={`aiu-vm-card ${statusClass}`}>
                          <div className="aiu-vm-label">
                            {card.label}: {card.displayPercent || '–'}
                          </div>

                          <div className="aiu-bar aiu-vm-bar">
                            <span
                              className={`aiu-bar-fill aiu-vm-fill ${fillClass}`}
                              style={{ width: `${barWidth}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          {/* Current Consumption */}
          <section className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-2 aiu-row-48">
              <h2 className="aiu-section-title m-0">{labels.consumptionTitle}</h2>

              <div className="d-flex align-items-center gap-3">
                <div className={`aiu-chip ${showConsumption ? 'opacity-0' : ''}`}>
                  {chips.consumption}
                </div>

                <Switch
                  checked={showConsumption}
                  onChange={() => setShowConsumption(!showConsumption)}
                  a11yLabel={labels.toggleSectionAriaLabel}
                  title={labels.toggleSectionTitle}
                />
              </div>
            </div>

            <div className={`aiu-collapsible ${showConsumption ? 'is-open' : 'is-closed'}`}>
              <div className="aiu-card p-3">
                {consumptionLog.map((block, index) => (
                  <div key={`${block.title}-${index}`} className={index ? 'mt-3' : ''}>
                    <h4 className="aiu-h4-title m-0">
                      {block.title}{' '}
                      <span className="aiu-muted">
                        ({block.time})
                      </span>
                    </h4>

                    <ul className="aiu-list">
                      {block.items.map((item, itemIndex) => (
                        <li key={itemIndex}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Suggested Consumption */}
          <section className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-2 aiu-row-48">
              <div className="d-flex align-items-center gap-2 aiu-suggested-title">
                <button
                  type="button"
                  className="date-qa-btn aiu-food-catalog-edit-icon-btn"
                  onClick={goToNutritionSettings}
                  aria-label="Open nutrition preferences"
                  title="Open nutrition preferences"
                >
                  <FcEngineering className="date-engineering-icon" />
                </button>

                <h2 className="aiu-section-title m-0">{labels.suggestedTitle}</h2>

                <button
                  type="button"
                  onClick={() => setShowSuggestedInfoModal(true)}
                  aria-label="Learn how your food suggestions are generated"
                  title="Learn how your food suggestions are generated"
                  style={{
                    lineHeight: 1,
                    fontSize: '1.25rem',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <FcAnswers />
                </button>
              </div>

              <div className="d-flex align-items-center gap-3">
                <div className={`aiu-chip ${showSuggested ? 'opacity-0' : ''}`}>
                  {chips.suggested}
                </div>

                <Switch
                  checked={showSuggested}
                  onChange={() => setShowSuggested(!showSuggested)}
                  a11yLabel={labels.toggleSectionAriaLabel}
                  title={labels.toggleSectionTitle}
                />
              </div>
            </div>

            <div className={`aiu-collapsible ${showSuggested ? 'is-open' : 'is-closed'}`}>
              <AiFoodCatalog
                onCategoryChange={handleFoodCategoryChange}
                onDietaryFilterChange={handleDietaryFilterChange}
                userDeficiencies={userDeficiencies}
                userProfile={userProfile}
              />

              {suggestedLog.length > 0 && (
                <div className="aiu-card p-3">
                  {suggestedLog.map((block, index) => (
                    <div key={`${block.title}-${index}`} className={index ? 'mt-3' : ''}>
                      <h4 className="aiu-h4-title m-0">{block.title}</h4>
                      <div className="aiu-p-text">{block.content}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* About to eat... */}
          <section className="mb-3">
            <div className="d-flex align-items-center justify-content-between mb-2 aiu-row-48">
              <div>
                <h2 className="aiu-section-title m-0 d-flex align-items-center gap-2">
                  {labels.aboutToEatTitle}
                  <button
                    type="button"
                    className="btn p-0 aiu-icon-btn"
                    onClick={handleRestoreDeletedItems}
                    disabled={!hasDeletions}
                    aria-label="Restore deleted items"
                    title="Restore deleted items"
                  >
                    <FcMakeDecision className="fs-4" aria-hidden="true" />
                  </button>
                </h2>

                <div className="aiu-muted">{labels.aboutToEatSubtitle}</div>
              </div>
            </div>

            <div className="d-flex flex-column gap-3">
              {visibleResults.map((item) => {
                const isOpen = expanded.has(item.id);
                const rotationClass = isOpen ? 'rotate-180' : '';

                const adjustItem =
                  filteredMealAdjustState?.items?.find(
                    (candidate) => candidate.id === item.id,
                  ) || null;

                const portionText = adjustItem ? getDisplayPortion(adjustItem) : item.servingText;

                const quantityDisplay = adjustItem
                  ? getDisplayQtyMultiplier(adjustItem)
                  : '1';

                return (
                  <div key={item.id} className="aiu-card">
                    <div className="d-flex align-items-center gap-3 p-3">
                      {item.img ? (
                        <img
                          src={item.img}
                          alt={item.title}
                          className="aiu-card-img"
                        />
                      ) : (
                        <div className="aiu-card-img aiu-card-img--placeholder">
                          {item.emoji}
                        </div>
                      )}

                      <div className="flex-grow-1 text-truncate">
                        <div className="aiu-item-title text-truncate">{item.title}</div>
                        <div className="aiu-muted">{portionText}</div>
                      </div>

                      <button
                        type="button"
                        className="btn p-0 aiu-icon-btn"
                        onClick={() => toggleIngredients(item.id)}
                        aria-label={labels.ingredientsBtnAriaLabel}
                        title={labels.ingredientsBtnTitle}
                      >
                        <FcExpand
                          className={`fs-4 aiu-rotatable ${rotationClass}`}
                        />
                      </button>

                      <button
                        type="button"
                        className="btn p-0 aiu-icon-btn"
                        aria-label="Todo list"
                        title="Item Nutrition"
                        onClick={() => openNutritionModalForItem(item.id)}
                      >
                        <FcTodoList className="fs-4" />
                      </button>

                      <button
                        type="button"
                        className="btn p-0 aiu-icon-btn"
                        aria-label="Delete"
                        title="Delete"
                        onClick={() => handleDeleteItem(item.id)}
                      >
                        <FcFullTrash className="fs-4" />
                      </button>

                      <div className="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          className="aiu-qty-btn"
                          onClick={() => handleDecrementPortion(item.id)}
                          aria-label={labels.decreaseQtyAria}
                        >
                          &ndash;
                        </button>
                        <span className="aiu-qty">{quantityDisplay}</span>
                        <button
                          type="button"
                          className="aiu-qty-btn"
                          onClick={() => handleIncrementPortion(item.id)}
                          aria-label={labels.increaseQtyAria}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div
                      className={`aiu-collapsible ${isOpen ? 'is-open' : 'is-closed'
                        } aiu-ingredients`}
                    >
                      <hr className="aiu-hr-soft mb-2" />

                      <div className="aiu-micro-pill aiu-micro-pill--bold">
                        {labels.ingredientsHeaderText}
                      </div>

                      <ul className="aiu-list aiu-ingredients-list">
                        {item.ingredients.map((ingredient, index) => (
                          <li key={index}>{ingredient}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Dev JSON Preview */}
            {devJsonOpen && (
              <>
                {(localMinimalJson || latestMinimalJson) && (
                  <div className="aiu-card p-3 mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="aiu-micro-pill aiu-micro-pill--bold">
                        Last parsed (minimal)
                      </div>

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary rounded-pill"
                        onClick={() =>
                          navigator.clipboard.writeText(
                            JSON.stringify(localMinimalJson || latestMinimalJson, null, 2),
                          )
                        }
                        title="Copy JSON"
                        aria-label="Copy JSON"
                      >
                        Copy JSON
                      </button>
                    </div>

                    <pre
                      style={{
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {JSON.stringify(localMinimalJson || latestMinimalJson, null, 2)}
                    </pre>
                  </div>
                )}

                {(localNutritionJson || latestNutritionJson) && (
                  <div className="aiu-card p-3 mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="aiu-micro-pill aiu-micro-pill--bold">
                        Last nutrition (AI)
                      </div>

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary rounded-pill"
                        onClick={() =>
                          navigator.clipboard.writeText(
                            JSON.stringify(localNutritionJson || latestNutritionJson, null, 2),
                          )
                        }
                        title="Copy nutrition JSON"
                        aria-label="Copy nutrition JSON"
                      >
                        Copy JSON
                      </button>
                    </div>

                    <pre
                      style={{
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {JSON.stringify(localNutritionJson || latestNutritionJson, null, 2)}
                    </pre>
                  </div>
                )}

                {adjustedNutritionJson && (
                  <div className="aiu-card p-3 mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="aiu-micro-pill aiu-micro-pill--bold">
                        Adjusted nutrition (after +/-)
                      </div>

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary rounded-pill"
                        onClick={() =>
                          navigator.clipboard.writeText(
                            JSON.stringify(adjustedNutritionJson, null, 2),
                          )
                        }
                        title="Copy adjusted nutrition JSON"
                        aria-label="Copy adjusted nutrition JSON"
                      >
                        Copy JSON
                      </button>
                    </div>

                    <pre
                      style={{
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {JSON.stringify(adjustedNutritionJson, null, 2)}
                    </pre>
                  </div>
                )}

                {aiLoading && (
                  <div className="aiu-card p-3 mt-3">
                    <div className="aiu-micro-pill aiu-micro-pill--bold">
                      Fetching nutrition (AI)…
                    </div>
                    <div className="aiu-muted">Waiting for Gemini response…</div>
                  </div>
                )}

                {!latestNutritionJson && latestNutritionResult && (
                  <div className="aiu-card p-3 mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="aiu-micro-pill aiu-micro-pill--bold">
                        Last nutrition (AI) — Error
                      </div>

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary rounded-pill"
                        onClick={() =>
                          navigator.clipboard.writeText(
                            JSON.stringify(latestNutritionResult, null, 2),
                          )
                        }
                        title="Copy result JSON"
                        aria-label="Copy result JSON"
                      >
                        Copy Result
                      </button>
                    </div>

                    <div className="aiu-muted">
                      {String(latestNutritionResult?.reason || 'unknown_error')}
                    </div>

                    <pre
                      style={{
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {JSON.stringify(latestNutritionResult, null, 2)}
                    </pre>
                  </div>
                )}
              </>
            )}
          </section>

          <hr className="aiu-hr-soft my-3" />

          {/* Nutrition Facts */}
          <section>
            <div className="aiu-nutrition mx-auto">
              <h1 className="aiu-nf-title">{effectiveNutritionFacts.title}</h1>

              {effectiveNutritionFacts.productLabel && (
                <div
                  className="text-muted"
                  style={{
                    fontSize: '0.95rem',
                    marginBottom: '4px',
                    fontWeight: '500',
                  }}
                >
                  {effectiveNutritionFacts.productLabel}
                </div>
              )}

              <div className="aiu-nf-sub">{effectiveNutritionFacts.subTitle}</div>

              <div className="d-flex justify-content-between align-items-baseline aiu-nf-serving-row">
                <div>{effectiveNutritionFacts.servingLeft}</div>
                <div>{effectiveNutritionFacts.servingRight}</div>
              </div>

              <hr className="aiu-hr-strong" />

              <div className="d-flex justify-content-between align-items-baseline">
                <div className="aiu-nf-amount">{effectiveNutritionFacts.amountPerServing}</div>

                {effectiveNutritionFacts.caloriesFromFat && (
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Calories from Fat {effectiveNutritionFacts.caloriesFromFat}
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-between align-items-center aiu-nf-calories-row">
                <div className="aiu-nf-calories">{effectiveNutritionFacts.caloriesLabel}</div>
                <div className="aiu-nf-calories-value">
                  {effectiveNutritionFacts.caloriesValue}
                </div>
              </div>

              <hr className="aiu-hr" />

              <div className="d-flex justify-content-end aiu-nf-dv-label">
                {effectiveNutritionFacts.dailyValueLabel}
              </div>

              <hr className="aiu-hr" />

              <div className="aiu-nf-body">
                {effectiveNutritionFacts.rows.map((row, index) =>
                  row.hr ? (
                    <hr key={`hr-${index}`} className="aiu-hr-soft" />
                  ) : (
                    <Row
                      key={`row-${index}`}
                      main={row.main}
                      mainBold={!!row.mainBold}
                      value={row.value}
                      pct={row.pct}
                      indent={row.indent === 1}
                      moreIndent={row.indent === 2}
                    />
                  ),
                )}
              </div>

              <hr className="aiu-hr-strong" />

              <div className="aiu-nf-facts">
                {effectiveNutritionFacts.facts.map((fact, index) => (
                  <React.Fragment key={`fact-${index}`}>
                    <Fact keyTxt={fact.keyTxt} val={fact.val} pct={fact.pct} />
                    {index < effectiveNutritionFacts.facts.length - 1 && (
                      <hr className="aiu-hr-soft" />
                    )}
                  </React.Fragment>
                ))}
              </div>

              <hr className="aiu-hr" />

              <div className="aiu-nf-footnote">{effectiveNutritionFacts.footnote}</div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="aiu-footer-wrap" aria-hidden={false}>
          <div className="aiu-footer">
            {/* Top-left icon */}
            <div className="aiu-footer-left">
              <button
                type="button"
                onClick={() => setShowHelpModal(true)}
                className="aiu-help-btn"
                aria-label="Open help and instructions"
                title="Click for help and instructions"
              >
                <FcAnswers className="aiu-answers-icon" aria-hidden="true" />
              </button>

              {isFirefoxBrowser && (
                <span
                  className="aiu-firefox-label"
                  aria-label="Firefox browser detected"
                  title="Firefox browser detected"
                >
                  <ImFirefox />
                </span>
              )}
            </div>

            <div className="aiu-footer-center">
              {aiLoading ||
                microphoneStatus === 'processing' ||
                cameraStatus === 'processing' ? (
                <div className="aiu-ai-status" role="status" aria-live="polite">
                  <FcAssistant className="aiu-ai-status-icon" aria-hidden="true" />
                  <div className="aiu-ai-status-text">
                    <div className="aiu-ai-status-title">{aiAssistTitle}</div>
                    <div className="aiu-ai-status-subtitle">{aiAssistSubtitle}</div>
                  </div>
                </div>
              ) : (
                shouldShowConfirmButton && (
                  <button
                    type="button"
                    className={ctaClass}
                    onClick={openConfirmAlert}
                    aria-label={labels.confirmBtnAriaLabel}
                    title={labels.confirmBtnTitle}
                    data-visible-after-send="true"
                  >
                    <FcOk className="aiu-confirm-icon me-2" aria-hidden />
                    {labels.confirmBtnText}
                  </button>
                )
              )}
            </div>

            {/* Input pill with camera + microphone + send */}
            <div
              className="aiu-pill-input"
              data-has-text={String(hasText || hasAttachedImage)}
            >
              <textarea
                ref={textareaRef}
                className={getTextareaClassName()}
                placeholder={effectiveInputPlaceholder}
                aria-label={labels.inputAriaLabel}
                value={inputValue}
                disabled={isTextInputDisabled}
                onChange={(event) => {
                  const newValue = event.target.value;
                  setInputValue(newValue);

                  if (newValue && newValue.trim().length > 0) {
                    setHasCompletedAiRound(false);
                  }
                }}
                onInput={autoResizeTextarea}
                onKeyDown={handleTextareaKeyDown}
                rows={1}
                data-max-rows={6}
              />

              <div className="controls">
                <AiUserInputCamera
                  ariaLabel={labels.openCameraAria}
                  title={labels.openCameraTitle}
                  onImageChange={handleCameraImageChange}
                  onImageTextReady={handleCameraImageTextReady}
                  onStatusChange={handleCameraStatusChange}
                  canInlineSend
                  sendAriaLabel={sendAriaLabel}
                  sendTitle={sendTitle}
                />

                <AiUserInputMicrophone
                  ariaLabel={labels.micAriaLabel}
                  title={labels.micTitle}
                  onTranscriptReady={handleMicrophoneTranscriptReady}
                  onStatusChange={handleMicrophoneStatusChange}
                  canInlineSend={false}
                  microphoneStatus={microphoneStatus}
                />
              </div>

              <div className="aiu-send-wrap">
                <button
                  type="button"
                  className="aiu-send-btn"
                  aria-label={sendAriaLabel}
                  title={sendTitle}
                  onClick={() => handleSend('send_button')}
                  disabled={!hasText}
                >
                  <FaPaperPlane className="aiu-send-icon" aria-hidden />
                </button>
              </div>
            </div>

            <div className="mt-2 text-center">
              {/* Dev JSON toggle button (disabled) */}
            </div>
          </div>
        </footer>

        {/* Confirm consumption alert modal */}
        <AlertModal
          open={isConfirmAlertOpen}
          preset="confirm_consumption"
          title={labels?.consumptionConfirmAlertTitle}
          message={labels?.consumptionConfirmMessage}
          variant="warning"
          requester={{ entityId: serviceName || 'AiUserInput' }}
          onClose={closeConfirmAlert}
          primaryAction={{
            label: labels?.consumptionConfirmPrimaryLabel,
            onClick: handleConfirmConsumptionFromAlert,
          }}
          secondaryAction={{
            label: labels?.consumptionConfirmSecondaryLabel,
            onClick: closeConfirmAlert,
          }}
          isBusy={isConfirming}
          busyContent={
            <div className="am-busy">
              <div className="am-spinner" aria-hidden="true" />
              <p className="am-message">
                Logging your meal and updating your daily intake…
              </p>
            </div>
          }
        />

        {/* Per-item nutrition modal */}
        <AlertModal
          open={!!nutritionModalItem}
          title={
            nutritionModalItem?.title
              ? String(nutritionModalItem.title)
              : 'Nutrition details'
          }
          message={
            nutritionModalItem ? (
              <div>
                {nutritionModalItem.portion && (
                  <div style={{ marginBottom: '8px' }}>
                    <div className="aiu-micro-pill aiu-micro-pill--bold">
                      Portion
                    </div>
                    <div>
                      {`${nutritionModalItem.portion.quantity ?? 'N/A'} ${nutritionModalItem.portion.unit ?? ''
                        }`.trim()}
                      {nutritionModalItem.portion.gramEquivalent
                        ? ` (${nutritionModalItem.portion.gramEquivalent} g)`
                        : ''}
                    </div>
                  </div>
                )}

                <div className="aiu-micro-pill aiu-micro-pill--bold">
                  Nutrients
                </div>

                <div
                  style={{
                    margin: 0,
                    maxHeight: '320px',
                    overflowY: 'auto',
                  }}
                >
                  {nutritionModalItem.nutrients &&
                    Object.keys(nutritionModalItem.nutrients).length ? (
                    <ul
                      className="aiu-list aiu-nutrition-modal-list"
                      style={{ margin: 0, paddingLeft: '1rem' }}
                    >
                      {Object.entries(nutritionModalItem.nutrients).map(
                        ([key, value]) => (
                          <li key={key}>
                            <strong>{formatNutrientKey(key)}:</strong>{' '}
                            {formatNutrientValue(value)}
                          </li>
                        ),
                      )}
                    </ul>
                  ) : (
                    <div className="aiu-muted">
                      No nutrient data available.
                    </div>
                  )}
                </div>
              </div>
            ) : (
              'Nutrition details unavailable.'
            )
          }
          variant="info"
          requester={{ entityId: 'nutrition-modal' }}
          onClose={() => setNutritionModalItem(null)}
          primaryAction={{
            label: 'Close',
            onClick: () => setNutritionModalItem(null),
          }}
        />

        {/* Help and Instructions Modal */}
        <AlertModal
          open={showHelpModal}
          title={
            isFirefoxBrowser
              ? '⚠️ Attention Firefox Users'
              : '📌 How to Use Nutrition AI'
          }
          message={
            isFirefoxBrowser ? (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <strong>Firefox Microphone Usage:</strong>
                  <p
                    style={{
                      marginTop: '8px',
                      marginBottom: '0',
                    }}
                  >
                    Firefox requires a longer press because of its
                    microphone permission handling. To use the
                    microphone, click and hold the button for about 2
                    seconds, then speak clearly into your microphone.
                  </p>
                </div>

                <div
                  style={{
                    marginBottom: '20px',
                    padding: '12px',
                    backgroundColor: '#005f2eff',
                    borderRadius: '4px',
                  }}
                >
                  <strong>💡 Recommendation:</strong>
                  <p
                    style={{
                      marginTop: '8px',
                      marginBottom: '0',
                    }}
                  >
                    For the best experience, we recommend using our
                    Nutrition AI model with Google Chrome.
                  </p>
                </div>

                <div>
                  <strong>📌 General Instructions for All Users:</strong>
                  <div style={{ marginTop: '12px' }}>
                    <strong>Chat Text Input:</strong>
                    <p
                      style={{
                        marginTop: '4px',
                        marginBottom: '8px',
                      }}
                    >
                      Always describe your food in natural language with
                      clear portions.
                    </p>
                    <p
                      style={{
                        marginLeft: '16px',
                        fontStyle: 'italic',
                        color: '#666',
                      }}
                    >
                      Example: &quot;I ate 200 grams of fish (grilled with olive oil), 1/2 cup of
                      rice (boiled), 1/2 cup of broccoli (steamed), and a 12 oz
                      lemonade (made with lemon juice, 1 teaspoon of sugar, and water).&quot;
                    </p>
                  </div>
                  <div style={{ marginTop: '12px' }}>
                    <strong>Image Input:</strong>
                    <p
                      style={{
                        marginTop: '4px',
                        marginBottom: '0',
                      }}
                    >
                      Take a photo of what you are about to eat. If the
                      AI reply shows incorrect portions, adjust the text
                      and resubmit for a final nutrition estimate.
                    </p>
                  </div>

                  <div style={{ marginTop: '12px' }}>
                    <strong>Microphone Input:</strong>
                    <p
                      style={{
                        marginTop: '4px',
                        marginBottom: '0',
                      }}
                    >
                      State specific portions. Once the text is returned,
                      adjust it if needed and submit.
                    </p>
                  </div>

                  <div style={{ marginTop: '12px' }}>
                    <strong>&quot;About to eat…&quot; Section:</strong>
                    <p
                      style={{
                        marginTop: '4px',
                        marginBottom: '0',
                      }}
                    >
                      You can edit the portions for better accuracy before
                      submitting what you ate.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: '12px' }}>
                  <strong>Chat Text Input:</strong>
                  <p
                    style={{
                      marginTop: '4px',
                      marginBottom: '8px',
                    }}
                  >
                    Always describe your food in natural language with
                    clear portions.
                  </p>
                  <p
                    style={{
                      marginLeft: '16px',
                      fontStyle: 'italic',
                      color: '#666',
                    }}
                  >
                    Example: &quot;I ate 200 grams of fish, 1/2 cup of
                    rice, 1/2 cup of broccoli, and a 12 oz
                    lemonade.&quot;
                  </p>
                </div>

                <div style={{ marginTop: '12px' }}>
                  <strong>Image Input:</strong>
                  <p
                    style={{
                      marginTop: '4px',
                      marginBottom: '0',
                    }}
                  >
                    Take a photo of what you are about to eat. If the AI
                    reply shows incorrect portions, adjust the text and
                    resubmit for a final nutrition estimate.
                  </p>
                </div>

                <div style={{ marginTop: '12px' }}>
                  <strong>Microphone Input:</strong>
                  <p
                    style={{
                      marginTop: '4px',
                      marginBottom: '0',
                    }}
                  >
                    State specific portions. Once the text is returned,
                    adjust it if needed and submit.
                  </p>
                </div>

                <div style={{ marginTop: '12px' }}>
                  <strong>&quot;About to eat…&quot; Section:</strong>
                  <p
                    style={{
                      marginTop: '4px',
                      marginBottom: '0',
                    }}
                  >
                    You can edit the portions for better accuracy before
                    submitting what you ate.
                  </p>
                </div>
              </div>
            )
          }
          variant="info"
          requester={{ entityId: 'help-modal' }}
          onClose={() => setShowHelpModal(false)}
          primaryAction={{
            label: 'Got it',
            onClick: () => setShowHelpModal(false),
          }}
        />

        {/* Macros Info Modal */}
        <AlertModal
          open={showMacrosInfoModal}
          title="How your Macros are calculated"
          message={(() => {
            const caloriesMacro = macros.find((m) => m.id === 'calories');
            const proteinMacro = macros.find((m) => m.id === 'protein');
            const carbsMacro = macros.find((m) => m.id === 'carbs');
            const fatMacro = macros.find((m) => m.id === 'fat');

            const getStatusExplanation = () => {
              const statuses = [];

              if (caloriesMacro && caloriesMacro.pct > 120) statuses.push('Calories');
              if (proteinMacro && proteinMacro.pct > 120) statuses.push('Protein');
              if (carbsMacro && carbsMacro.pct >= 80 && carbsMacro.pct <= 120)
                statuses.push('Carbs are close to target');
              if (fatMacro && fatMacro.pct > 150)
                statuses.push('Fat intake is clearly higher than recommended');

              if (statuses.length === 0) return '';
              return `${statuses.join(', ')}.`;
            };

            return (
              <div className="text-white" style={{ lineHeight: 1.6 }}>
                <h5
                  style={{
                    color: '#fff',
                    marginBottom: '1rem',
                  }}
                >
                  Personal targets, based on your profile
                </h5>

                <p style={{ marginBottom: '1rem' }}>
                  Your daily targets for calories, protein, carbs, and fat are calculated from:
                </p>

                <ul
                  style={{
                    marginBottom: '1.5rem',
                    paddingLeft: '1.5rem',
                  }}
                >
                  <li>Your age, sex, height, weight, and activity level</li>
                  <li>The recommendations guidelines for your life stage</li>
                </ul>

                <h5
                  style={{
                    color: '#fff',
                    marginBottom: '1rem',
                  }}
                >
                  What you have eaten today
                </h5>

                <p style={{ marginBottom: '1rem' }}>
                  As you log food, we calculate how much energy and nutrients you have consumed so
                  far today.
                </p>

                <div
                  style={{
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                  }}
                >
                  {caloriesMacro && (
                    <p style={{ marginBottom: '0.5rem' }}>
                      <strong style={{ color: '#4ade80' }}>Calories:</strong> You have eaten{' '}
                      <strong>{caloriesMacro.value} kcal</strong> today. Your daily target is{' '}
                      <strong>{caloriesMacro.target} kcal</strong> (
                      {Math.round(caloriesMacro.pct)}
                      % of your goal).
                    </p>
                  )}

                  {proteinMacro && (
                    <p style={{ marginBottom: '0.5rem' }}>
                      <strong style={{ color: '#60a5fa' }}>Protein:</strong> You have eaten{' '}
                      <strong>{proteinMacro.value}g</strong> out of{' '}
                      <strong>{proteinMacro.target}g</strong> (
                      {Math.round(proteinMacro.pct)}
                      %).
                    </p>
                  )}

                  {carbsMacro && (
                    <p style={{ marginBottom: '0.5rem' }}>
                      <strong style={{ color: '#fbbf24' }}>Carbs:</strong> You have eaten{' '}
                      <strong>{carbsMacro.value}g</strong> out of{' '}
                      <strong>{carbsMacro.target}g</strong> (
                      {Math.round(carbsMacro.pct)}
                      %).
                    </p>
                  )}

                  {fatMacro && (
                    <p style={{ marginBottom: 0 }}>
                      <strong style={{ color: '#f87171' }}>Fat:</strong> You have eaten{' '}
                      <strong>{fatMacro.value}g</strong> out of{' '}
                      <strong>{fatMacro.target}g</strong> (
                      {Math.round(fatMacro.pct)}
                      %).
                    </p>
                  )}
                </div>

                {getStatusExplanation() && (
                  <p
                    style={{
                      marginBottom: '1.5rem',
                      fontStyle: 'italic',
                      color: '#fbbf24',
                    }}
                  >
                    {getStatusExplanation()}
                  </p>
                )}

                <h5
                  style={{
                    color: '#fff',
                    marginBottom: '1rem',
                  }}
                >
                  What the colors mean
                </h5>

                <p style={{ marginBottom: '0.5rem' }}>
                  We use colors to help you see at a glance how each macro compares to your daily
                  target:
                </p>

                <ul
                  style={{
                    marginBottom: '1rem',
                    paddingLeft: '1.5rem',
                  }}
                >
                  <li style={{ marginBottom: '0.5rem' }}>
                    <strong style={{ color: '#4ade80' }}>Green — On track</strong> (about 80–120%
                    of your target). You are within a healthy daily range.
                  </li>

                  <li style={{ marginBottom: '0.5rem' }}>
                    <strong style={{ color: '#fbbf24' }}>Orange — Above or below goal</strong>{' '}
                    (more than 20% away from your target). You may want to adjust this macro over
                    the rest of the day.
                  </li>

                  <li style={{ marginBottom: '0.5rem' }}>
                    <strong style={{ color: '#f87171' }}>Red — Well above goal</strong> (more than
                    150% of your target). This usually means you have had more than is recommended
                    for the day.
                  </li>

                  <li>
                    <strong style={{ color: '#9ca3af' }}>Gray — No target</strong>. We show the
                    amount you have eaten, but do not have a clear guideline for this macro yet.
                  </li>
                </ul>

                <p
                  style={{
                    marginBottom: 0,
                    fontSize: '0.9rem',
                    fontStyle: 'italic',
                  }}
                >
                  These targets are guidelines, not rules. Use them to spot patterns and decide
                  where you want to make changes over time.
                </p>
              </div>
            );
          })()}
          variant="info"
          requester={{ entityId: 'macros-info-modal' }}
          onClose={() => setShowMacrosInfoModal(false)}
          primaryAction={{
            label: 'Got it',
            onClick: () => setShowMacrosInfoModal(false),
          }}
        />

        {/* Vitamins & Minerals Info Modal */}
        <AlertModal
          open={showVitaminsInfoModal}
          title="How your vitamins and minerals are calculated"
          message={
            <div className="text-white" style={{ lineHeight: 1.6 }}>
              <p style={{ marginBottom: '1rem' }}>
                Your vitamin and mineral status is based on two things:
              </p>

              <ol
                style={{
                  marginBottom: '1.5rem',
                  paddingLeft: '1.5rem',
                }}
              >
                <li style={{ marginBottom: '0.5rem' }}>
                  The foods and drinks you log during the day.
                </li>
                <li>
                  Personal daily targets built from scientific guidelines for your age, sex, and
                  body size (based on Nordic Nutrition Recommendations 2023).
                </li>
              </ol>

              <h5
                style={{
                  color: '#fff',
                  marginBottom: '1rem',
                  marginTop: '1.5rem',
                }}
              >
                For each vitamin and mineral we:
              </h5>

              <ul
                style={{
                  marginBottom: '1.5rem',
                  paddingLeft: '1.5rem',
                }}
              >
                <li style={{ marginBottom: '0.5rem' }}>
                  Add up everything you have consumed from all logged foods for the current day.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Match that nutrient to a daily target for your life stage.
                  <ul
                    style={{
                      marginTop: '0.5rem',
                      paddingLeft: '1.5rem',
                    }}
                  >
                    <li>
                      If there is a recommended intake (RI) or adequate intake (AI), we treat that
                      as the &quot;goal&quot; you are aiming for.
                    </li>
                    <li>
                      If there is a chronic disease risk reduction (CDRR), such as for sodium/salt,
                      we treat that as a &quot;do not exceed&quot; limit.
                    </li>
                  </ul>
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Divide today&apos;s intake by your daily target and convert it to a percentage.
                  <ul
                    style={{
                      marginTop: '0.5rem',
                      paddingLeft: '1.5rem',
                    }}
                  >
                    <li>100% means you are exactly at the daily target.</li>
                    <li>Above 100% means you are above the target.</li>
                    <li>Below 100% means you are below the target.</li>
                  </ul>
                </li>
                <li>
                  In a few cases, when your intake is less than 1% of the target, the number is
                  shown as &quot;&lt;1%&quot;. This does not mean it is literally zero; it only
                  means it is very small compared to the daily goal.
                </li>
              </ul>

              <h5
                style={{
                  color: '#fff',
                  marginBottom: '1rem',
                  marginTop: '1.5rem',
                }}
              >
                Why you see these colors
              </h5>

              <p style={{ marginBottom: '0.5rem' }}>
                The colored cards help you quickly understand your overall micronutrient pattern:
              </p>

              <ul
                style={{
                  marginBottom: '1rem',
                  paddingLeft: '1.5rem',
                }}
              >
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong style={{ color: '#22c55e' }}>Green (on track):</strong> Your intake is
                  close to your daily target (roughly 80–120% of your goal). This suggests that, for
                  that nutrient, today&apos;s intake is within a healthy range.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong style={{ color: '#fbbf24' }}>Yellow/Orange (caution):</strong> Your
                  intake is noticeably below the recommended level (70–79%) or clearly above it.
                  This is a gentle reminder to keep an eye on this nutrient.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong style={{ color: '#f87171' }}>Red (high concern):</strong> Your intake is
                  far below target (&lt;70%) or well above what is recommended for the day (&gt;120%
                  ). This signals a pattern you may want to discuss with a healthcare professional if
                  it is frequent.
                </li>
                <li>
                  <strong style={{ color: '#9ca3af' }}>Gray (no clear target):</strong> We can
                  measure how much you have consumed, but we do not have a strong scientific daily
                  target for your profile. In these cases, we show the value but do not color it as
                  good or bad.
                </li>
              </ul>

              <h5
                style={{
                  color: '#fff',
                  marginBottom: '1rem',
                  marginTop: '1.5rem',
                }}
              >
                How to use this view
              </h5>

              <p style={{ marginBottom: '0.5rem' }}>
                Use the vitamins and minerals view to:
              </p>

              <ul
                style={{
                  marginBottom: '1.5rem',
                  paddingLeft: '1.5rem',
                }}
              >
                <li style={{ marginBottom: '0.5rem' }}>
                  Spot nutrients that are consistently low, so you can talk with a professional
                  about food choices or supplements if needed.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Notice nutrients that are frequently very high, especially where there are known
                  upper safety limits.
                </li>
                <li>
                  Track patterns over time rather than focusing on a single day. Being a bit above
                  or below a target on one day is normal; trends over weeks are more important.
                </li>
              </ul>

              <p
                style={{
                  marginBottom: 0,
                  fontSize: '0.9rem',
                  fontStyle: 'italic',
                }}
              >
                These results are estimates based on your logged intake and your profile, not a
                diagnosis. They are meant to support informed decisions and conversations with
                qualified health professionals, not replace them.
              </p>
            </div>
          }
          variant="info"
          requester={{ entityId: 'vitamins-info-modal' }}
          onClose={() => setShowVitaminsInfoModal(false)}
          primaryAction={{
            label: 'Got it',
            onClick: () => setShowVitaminsInfoModal(false),
          }}
        />

        <AlertModal
          open={showSuggestedInfoModal}
          title="How to use your food suggestions"
          message={
            <div className="text-white" style={{ lineHeight: 1.6 }}>
              <p style={{ marginBottom: '1rem' }}>
                This view works a little like having a dietitian looking over your day. It
                highlights foods and meals that can gently steer you toward a more balanced intake
                of vitamins and minerals.
              </p>

              <h5
                style={{
                  color: '#fff',
                  marginBottom: '0.75rem',
                }}
              >
                What these suggestions are based on
              </h5>

              <ul
                style={{
                  marginBottom: '1rem',
                  paddingLeft: '1.5rem',
                }}
              >
                <li style={{ marginBottom: '0.5rem' }}>
                  We look at what you have logged so far today and identify vitamins and minerals
                  where your intake is clearly lower than your personal daily targets.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  The suggested foods and meals are chosen because they are naturally good sources
                  of the nutrients you are currently lacking.
                </li>
                <li>
                  In simple terms: the list is designed to give you ideas that can help “fill in the
                  gaps” in a practical way.
                </li>
              </ul>

              <h5
                style={{
                  color: '#fff',
                  marginBottom: '0.75rem',
                  marginTop: '1rem',
                }}
              >
                Choosing what you feel like eating
              </h5>

              <ul
                style={{
                  marginBottom: '1rem',
                  paddingLeft: '1.5rem',
                }}
              >
                <li style={{ marginBottom: '0.5rem' }}>
                  Use the category dropdown to choose the type of food or meal you are looking for
                  (for example breakfast, snack, or a certain food group).
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Use the filters (for example vegetarian, lactose free, gluten free) to make sure
                  the suggestions fit your way of eating.
                </li>
                <li>
                  Even when you narrow things down with the dropdowns and filters, we still try to
                  offer options that contribute meaningful amounts of the vitamins and minerals you
                  need more of. The suggestions are there to support you — you always remain free to
                  choose other foods.
                </li>
              </ul>

              <h5
                style={{
                  color: '#fff',
                  marginBottom: '0.75rem',
                  marginTop: '1rem',
                }}
              >
                Refreshing your ideas
              </h5>

              <p style={{ marginBottom: '1rem' }}>
                When you tap the <strong>refresh suggestions button</strong> (the small
                branching/decision icon), the list of ideas is updated. The first one or two
                suggestions after a refresh are especially focused on the vitamins and minerals you
                are most lacking at that moment, while the other options still keep your nutrient
                needs in mind but add more variety.
              </p>

              <h5
                style={{
                  color: '#fff',
                  marginBottom: '0.75rem',
                  marginTop: '1rem',
                }}
              >
                Adjusting your nutrition settings
              </h5>

              <p style={{ marginBottom: '0.5rem' }}>
                Tap the orange settings icon next to &quot;Suggested Consumption&quot; (the
                engineering/gear icon) to open your nutrition preferences.
              </p>

              <ul
                style={{
                  marginBottom: '1.25rem',
                  paddingLeft: '1.5rem',
                }}
              >
                <li style={{ marginBottom: '0.5rem' }}>
                  There you can review or change things like your goals, dietary pattern, and foods
                  you want to minimise or avoid.
                </li>
                <li>
                  Updating these settings helps the suggestions better reflect both your nutritional
                  needs and your everyday preferences.
                </li>
              </ul>

              <p
                style={{
                  marginBottom: 0,
                  fontSize: '0.9rem',
                  fontStyle: 'italic',
                }}
              >
                Think of this section as a gentle guide, not a list of rules. The goal is to give
                you realistic options that can move you closer to a well-balanced intake of vitamins
                and minerals over time.
              </p>
            </div>
          }
          variant="info"
          requester={{ entityId: 'suggested-info-modal' }}
          onClose={() => setShowSuggestedInfoModal(false)}
          primaryAction={{
            label: 'Got it',
            onClick: () => setShowSuggestedInfoModal(false),
          }}
        />
      </div>
    </div>
  );
}

function Switch({ checked, onChange, a11yLabel, title }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className="aiu-toggle"
      onClick={onChange}
      title={title}
      aria-label={a11yLabel}
    >
      <span className="aiu-handle" />
    </button>
  );
}

function Row({ main, mainBold, value, pct, indent, moreIndent }) {
  const indentClass = moreIndent ? 'aiu-pl-32' : indent ? 'aiu-pl-16' : '';

  return (
    <div className={`d-flex justify-content-between aiu-nf-row ${indentClass}`}>
      <div>
        {main ? (
          <>
            <b className={mainBold ? 'fw-bold' : ''}>{main}</b>
            {value}
          </>
        ) : (
          value
        )}
      </div>
      <div className="fw-bold">{pct || ''}</div>
    </div>
  );
}

function Fact({ keyTxt, val, pct }) {
  return (
    <div className="d-flex justify-content-between aiu-nf-row">
      <div>
        {keyTxt} {val}
      </div>
      <div>{pct}</div>
    </div>
  );
}

export default function AiUserInput() {
  return (
    <AiUserInputNutritionDataProvider>
      <AiUserInputView />
    </AiUserInputNutritionDataProvider>
  );
}
